<template>
  <div>
    <div v-if="isLoading" class="overlay">
      <loader />
    </div>

    <p v-if="!isCustomerValid" class="error-message">
      {{ i18n.t('/Validations/Customer Fields Required Message') }}
    </p>
    <div class="actions">
      <app-button
        :disabled="!isUpdatable"
        data-qa="save-purchase-order-button"
        @click="savePurchaseOrder"
        >{{ i18n.t('/Common/Save') }}</app-button
      >
      <app-button
        :disabled="!sendOrderIdsEnabled"
        data-qa="send-order-ids-button"
        @click="sendOrderIds"
        >Order versturen</app-button
      >

      <modal v-if="showOrderModal" :title="showOrderModalResultTitle" @close="closeShowOrderModal">
        <template v-if="!!orderResult?.TransactionId">
          <div v-if="progressResult?.Errors">
            <p>Bestellen van auto is mislukt. Foutmelding:</p>
            <p class="error-details">
              {{ progressResult?.Errors }}
            </p>
          </div>
          <progress-bar
            v-else-if="progressResult?.ProgressPercentage < 100"
            :progress="progressResult?.ProgressPercentage ?? 0"
          />
          <p v-else>De order is succesvol geplaatst.</p>
        </template>
        <template v-else>
          <p v-if="orderResult?.Success">De order is succesvol geplaatst.</p>
          <p v-else>Bestelling mislukt</p>

          <div v-if="!orderResult?.Success">
            <strong> Error code: {{ orderResult?.ErrorCode }} </strong>
            <p>
              {{ orderResult?.Message }}
            </p>
          </div>
        </template>

        <app-button data-qa="close-order-modal-button" @click="showOrderModal = false">{{
          i18n.t('/Common/Close')
        }}</app-button>
      </modal>
    </div>
    <expected-delivery-date-prompt :show="showNoLiBPrompt" @close="showNoLiBPrompt = false" />
  </div>
</template>

<script>
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import purchaseOrderService from '@/services/purchaseOrderService.js'
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'
import ProgressBar from '@/components/generic/presentational/atoms/progressBar/ProgressBar.vue'
import ExpectedDeliveryDatePrompt from '@/components/purchaseOrder/presentational/molecules/expectedDeliveryDatePrompt/ExpectedDeliveryDatePrompt.vue'
import CheckExpectedDeliveryDatePurchaseOrder from '@/mixins/checkExpectedDeliveryDatePurchaseOrder'
import { mapState, mapGetters } from 'vuex'

export default {
  components: {
    AppButton,
    Modal,
    Loader,
    ProgressBar,
    ExpectedDeliveryDatePrompt,
  },
  mixins: [CheckExpectedDeliveryDatePurchaseOrder],
  props: {
    isCustomerValid: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['has-customer-type'],
  data() {
    return {
      orderResult: {
        Success: false,
        ErrorCode: '',
        Message: '',
        TransactionId: '',
      },
      progressResult: {
        Status: '',
        ProgressPercentage: 0,
        Errors: '',
      },
      retryCount: 0,
      maxRetries: 3,
      showOrderModal: false,
      isLoading: false,
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      progressCheckTimeout: null,
    }
  },
  computed: {
    isUpdatable() {
      return (
        this.isCustomerValid &&
        !this.isLoading &&
        this.purchaseOrder?.Details?.Dealer?.EstablishmentID &&
        this.purchaseOrder?.Details?.Car?.Amount &&
        this.purchaseOrder?.Status !== 'PENDING' &&
        this.purchaseOrder?.Status !== 'PURCHASEORDER'
      )
    },
    sendOrderIdsEnabled() {
      return (
        this.purchaseOrder.Status === 'CONCEPT' &&
        this.configuration?.Status?.IsValid &&
        this.isUpdatable &&
        this.purchaseOrder?.PurchaseOrderId &&
        !this.formIsDirty
      )
    },
    ...mapState('purchaseOrder', ['purchaseOrder', 'formIsDirty']),
    ...mapState('configuration', ['configuration']),
    ...mapGetters({
      hasCustomerType: 'purchaseOrder/hasCustomerType',
    }),
    showOrderModalResultTitle() {
      if (this.orderResult?.TransactionId) return 'Voortgang van uw bestelling(en)'

      return 'Resultaat van uw bestelling'
    },
  },
  methods: {
    async savePurchaseOrder() {
      this.isLoading = true
      if (!this.hasCustomerType) {
        this.$emit('has-customer-type', false)
        this.isLoading = false
        return
      }
      if (this.$route.name == 'new-purchase-order-page') {
        // create new PurchaseOrder and navigate to it
        const result = await purchaseOrderService.createPurchaseOrder()
        let params = {
          purchaseOrderId: result.PurchaseOrderId,
          establishmentId: this.$route.params.establishmentId,
        }
        this.$router.push({ name: 'purchase-order-page', params })
      } else {
        await purchaseOrderService.updatePurchaseOrder()
      }
      this.isLoading = false
    },
    async sendOrderIds() {
      if (!this.expectedDeliveryDateCheck()) {
        return
      }

      const totalOrderPrice = this.purchaseOrder?.MetaData?.Prices?.PurchaseOrderPrice?.Incl
      let confirmMessage = 'Ja, ik weet zeker dat ik deze order wil versturen.'
      if (totalOrderPrice)
        confirmMessage += ` Het totale bedrag van de order bedraagt ${this.i18n.n(
          totalOrderPrice,
          'currency'
        )}.`
      if (confirm(confirmMessage)) {
        this.isLoading = true

        try {
          this.orderResult = await purchaseOrderService.requestPurchaseOrder()
        } catch (error) {
          this.orderResult = {
            Success: false,
            Message: error?.response?.data?.Message || 'Onverwacht resultaat',
          }
        } finally {
          // fetch the Purchase order again to update the new Status
          if (!this.orderResult?.TransactionId) {
            await purchaseOrderService.getPurchaseOrder(this.purchaseOrder?.PurchaseOrderId)
            this.isLoading = false
          }
        }

        this.showOrderModal = true
        await this.checkProgress(true)
      }
    },
    async closeShowOrderModal() {
      this.showOrderModal = false
      if (this.orderResult?.TransactionId && this.progressCheckTimeout) {
        await this.finishProgressCheck()
      }
    },
    async checkProgress(force) {
      if (!this.orderResult?.TransactionId) return

      let doCheck =
        force ||
        (this.showOrderModal &&
          (this.progressResult?.Status === 'PROCESSING' ||
            this.progressResult?.Status === 'UNKNOWN') &&
          !this.progressResult?.Errors)

      if (doCheck) {
        try {
          // Fetch progress result from the backend
          this.progressResult = await purchaseOrderService.getPurchaseOrderProgress(
            this.purchaseOrder?.PurchaseOrderId,
            this.orderResult?.TransactionId
          )

          // Check if status is UNKNOWN
          if (this.progressResult.Status === 'UNKNOWN' && this.retryCount < this.maxRetries) {
            this.retryCount += 1

            // Retry after X seconds
            this.progressCheckTimeout = setTimeout(() => this.checkProgress(false), 3000)
          } else if (
            this.progressResult.Status === 'UNKNOWN' &&
            this.retryCount >= this.maxRetries
          ) {
            // Max retries reached, mark as an error and stop retrying
            this.progressResult.Errors =
              'De order kon niet worden verwerkt. Probeer het later opnieuw.'
            await this.finishProgressCheck()
          } else {
            // Reset retry count if we get a valid status other than UNKNOWN
            this.retryCount = 0

            // Continue checking if progress is still ongoing
            if (this.progressResult.ProgressPercentage < 100) {
              this.progressCheckTimeout = setTimeout(() => this.checkProgress(false), 3000)
            } else {
              // Finish progress check if completed
              await this.finishProgressCheck()
            }
          }
        } catch (error) {
          console.error('CheckProgress error: ', error)
          this.progressResult = {
            ProgressPercentage: 0,
            Errors: 'Onverwacht resultaat',
            Status: 'ERROR',
          }
          this.isLoading = false
        }
      } else {
        await this.finishProgressCheck()
      }
    },
    async finishProgressCheck() {
      clearTimeout(this.progressCheckTimeout)
      this.progressCheckTimeout = null
      await purchaseOrderService.getPurchaseOrder(this.purchaseOrder?.PurchaseOrderId)
      this.isLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
.overlay {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: 999;

  .loader {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translateX(-50%);
  }
}

.actions {
  display: flex;
  justify-content: space-between;
  margin-bottom: 5em;
}

.error-message {
  color: $color-danger;
}

.error-details {
  white-space: pre-wrap;
}
</style>
